import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { nextMark } from '../lib/episodeProgress'
import { endWatchSession } from '../lib/watchSessions'
import { readPendingWatch, clearPendingWatch } from '../lib/watchReturn'
import { WatchingNowIcon } from './WatchingNowIcon'
import { Z } from '../lib/zLayers'

// ── WatchingCheckIn ───────────────────────────────────────────────────────────
// The in-app half of the "I'm watching this now" check-in (Jason 2026-09-19).
// watching-checkin-sweep.js (Netlify scheduled function) covers the app-closed
// case via push; this covers the app-STILL-OPEN case, where a push can land as a
// native OS notification with no in-app surface to act on. Polls the signed-in
// user's own OPEN sessions on a plain interval (no realtime channel — this is a
// once-per-episode-length event, not a stream) and offers the same one-tap mark
// WatchReturnPrompt uses once a session's due_at has passed.
//
// Deliberately does NOT also fire on visibilitychange/focus like WatchReturnPrompt
// — this prompt's trigger (a runtime elapsing) can and often will happen while the
// tab is genuinely foregrounded and idle (paused on the same page, watching TV on
// another screen), so it needs its own clock, not a "welcome back" heuristic.
const POLL_MS = 60000

export function WatchingCheckIn({ user, wl }) {
  const [session, setSession] = useState(null)
  const dismissedRef = useRef(new Set())

  useEffect(() => {
    if (!user?.id) return
    let alive = true
    const check = async () => {
      const { data } = await supabase
        .from('watch_sessions')
        .select('id, show_id, show_name, season, episode, due_at')
        .eq('user_id', user.id).is('ended_at', null).lte('due_at', new Date().toISOString())
        .order('due_at', { ascending: true }).limit(1)
      if (!alive) return
      const s = data?.[0]
      if (s && !dismissedRef.current.has(s.id)) setSession(s)
    }
    check()
    const id = setInterval(check, POLL_MS)
    const onMarked = (e) => { const sid = e.detail?.showId; if (sid != null) setSession(s => (s && s.show_id === sid ? null : s)) }
    window.addEventListener('nextep:progress-marked', onMarked)
    return () => { alive = false; clearInterval(id); window.removeEventListener('nextep:progress-marked', onMarked) }
  }, [user?.id])

  if (!session) return null

  // This card and WatchReturnPrompt's older pending-watch heuristic can both
  // end up armed for the same show (Jason 2026-09-20: "two prompts asking if
  // a show is completed") — once THIS one has been acted on, drop a matching
  // pending-watch flag so its stale "back from X?" doesn't immediately follow.
  const clearMatchingPendingWatch = () => {
    const p = readPendingWatch()
    if (p?.showId === session.show_id) clearPendingWatch()
  }

  const dismiss = () => {
    dismissedRef.current.add(session.id)
    endWatchSession(user.id, session.show_id)
    clearMatchingPendingWatch()
    setSession(null)
  }
  const mark = () => {
    const prog = wl?.progress?.[session.show_id]
    const show = wl?.items?.find(s => s.id === session.show_id)
    if (prog && show && prog.season === session.season && prog.episode === session.episode) {
      const { prog: nextProg, opts } = nextMark(show, prog)
      wl.saveProgress(session.show_id, nextProg, opts)
    }
    dismissedRef.current.add(session.id)
    clearMatchingPendingWatch()
    setSession(null)   // endWatchSession already runs inside saveProgress's mark path
  }

  return createPortal((
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(var(--nav-h) + 8px)',
      zIndex: Z.toast, maxWidth: 460, margin: '0 auto', fontFamily: 'var(--font)',
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--amber) 14%, #14141f), color-mix(in srgb, var(--amber) 6%, #0d0d16))',
      border: '1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius: 'var(--toast-radius)',
      boxShadow: '0 14px 38px rgba(0,0,0,0.55), 0 4px 18px rgba(var(--amber-rgb),0.18), inset 0 1px 0 rgba(var(--ink-rgb),0.06)',
      backdropFilter: 'blur(16px) saturate(1.3)', WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
      padding: '12px 16px 13px',
      '--nxa': 'wciIn 0.3s cubic-bezier(0.22,1.2,0.36,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 13 }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          width: 'var(--toast-chip-size)', height: 'var(--toast-chip-size)', borderRadius: 'var(--radius-full)',
          background: 'rgba(var(--amber-rgb),0.14)', border: '1px solid rgba(var(--amber-rgb),0.35)',
        }}><WatchingNowIcon size={20} color="var(--amber)" /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'var(--amber)', fontSize: 'var(--toast-eyebrow-size)', fontWeight: 'var(--toast-eyebrow-weight)', letterSpacing: 'var(--toast-eyebrow-tracking)', textTransform: 'uppercase', marginBottom: 2 }}>
            Did you finish it?
          </div>
          <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ fontWeight: 700 }}>{session.show_name || 'That episode'}</span> S{session.season}·E{session.episode}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 9 }}>
        <button onClick={dismiss} style={{
          flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          padding: '11px 0', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 700, fontFamily: 'var(--font)',
        }}>Not yet</button>
        <button onClick={mark} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: 'var(--amber)', border: 'none', borderRadius: 'var(--radius)',
          padding: '11px 0', cursor: 'pointer', color: 'var(--amber-ink)', fontSize: 13.5, fontWeight: 800, fontFamily: 'var(--font)',
          boxShadow: '0 4px 14px rgba(var(--amber-rgb),0.34)',
        }}>Mark watched</button>
      </div>
      <style>{'@keyframes wciIn{0%{opacity:0;transform:translateY(20px) scale(0.96)}100%{opacity:1;transform:translateY(0) scale(1)}}'}</style>
    </div>
  ), document.body)
}
